import DashboardLayout from '@/components/DashboardLayout';
import { router } from '@inertiajs/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  GraduationCap, Plus, Settings, Check,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import { toast } from 'sonner';

interface StudentData {
  id: number;
  name: string;
  enrolled: boolean;
  enrollment_expires: string | null;
}

interface EnrollmentData {
  id: number;
  student: { id: number; name: string };
  status: string;
  price_cents: number;
  formatted_price: string;
  paid_at: string | null;
  expires_at: string | null;
  days_until_expiry: number | null;
  is_expired: boolean;
  created_at: string;
}

interface Props {
  enrollments: EnrollmentData[];
  pagination: { current_page: number; total_pages: number; total_count: number; per_page: number };
  filters: { status: string };
  students: StudentData[];
  settings: { requires_enrollment: boolean; enrollment_price_cents: number; enrollment_validity_months: number };
}

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Pendente', variant: 'secondary' },
  active: { label: 'Matriculado', variant: 'default' },
};

function formatCurrency(cents: number): string {
  if (cents === 0) return 'Gratuita';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

function parseCentsInput(raw: string): number {
  return parseInt(raw.replace(/\D/g, '') || '0', 10);
}

function formatCentsInput(cents: number): string {
  if (cents === 0) return '';
  return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Index({ enrollments, pagination, filters, students, settings }: Props) {
  const [statusFilter, setStatusFilter] = useState(filters.status);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [studentFilter, setStudentFilter] = useState('');
  const [discountCents, setDiscountCents] = useState(0);

  // Settings form
  const [reqEnrollment, setReqEnrollment] = useState(settings.requires_enrollment);
  const [priceCents, setPriceCents] = useState(settings.enrollment_price_cents);
  const [validityMonths, setValidityMonths] = useState(String(settings.enrollment_validity_months));

  const unenrolledStudents = students.filter(s => !s.enrolled);

  const stats = {
    total: enrollments.length,
    active: enrollments.filter(e => e.status === 'active').length,
    expiring: enrollments.filter(e => e.status === 'active' && e.days_until_expiry !== null && e.days_until_expiry <= 30).length,
    expired: enrollments.filter(e => e.status === 'expired' || e.is_expired).length,
  };

  const applyFilter = (status: string) => {
    setStatusFilter(status);
    router.get('/enrollments', { status }, { preserveState: true });
  };

  const enrollStudent = () => {
    if (!selectedStudentId) { toast.error('Selecione um aluno'); return; }
    router.post('/enrollments', { student_id: selectedStudentId, discount_cents: discountCents }, {
      onSuccess: () => { setEnrollOpen(false); setSelectedStudentId(''); setDiscountCents(0); setStudentFilter(''); },
    });
  };

  const handleAction = (id: number, action: string) => {
    router.patch(`/enrollments/${id}`, { action_type: action });
  };

  const saveSettings = () => {
    router.patch('/enrollments/update_settings', {
      requires_enrollment: reqEnrollment,
      enrollment_price_cents: priceCents,
      enrollment_validity_months: parseInt(validityMonths) || 12,
    }, {
      onSuccess: () => { setSettingsOpen(false); toast.success('Configuracoes salvas!'); },
    });
  };

  return (
    <DashboardLayout>
      <div className="p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <GraduationCap className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Matriculas</h1>
              <p className="text-muted-foreground">
                {settings.requires_enrollment ? `Obrigatoria - ${formatCurrency(settings.enrollment_price_cents)}` : 'Desativada'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
              <Settings className="w-4 h-4 mr-1" /> Configurar
            </Button>
            <Button size="sm" onClick={() => setEnrollOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Matricular Aluno
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Matriculados</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Pendentes</p>
              <p className="text-2xl font-bold text-amber-600">{enrollments.filter(e => e.status === 'pending').length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex gap-3 mb-4">
          <Select value={statusFilter} onValueChange={applyFilter}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Matriculados</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {enrollments.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Nenhuma matricula encontrada</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Data Matricula</TableHead>
                    <TableHead className="text-right">Acoes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrollments.map(e => {
                    const statusInfo = STATUS_MAP[e.status] || STATUS_MAP.pending;
                    return (
                      <TableRow key={e.id}>
                        <TableCell className="font-medium">{e.student.name}</TableCell>
                        <TableCell>
                          <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                        </TableCell>
                        <TableCell>{e.formatted_price}</TableCell>
                        <TableCell>
                          {e.paid_at ? format(new Date(e.paid_at), 'dd/MM/yyyy', { locale: ptBR }) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {e.status === 'pending' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs text-green-600" onClick={() => handleAction(e.id, 'activate')}>
                              <Check className="w-3 h-3 mr-1" /> Confirmar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <span className="text-sm text-muted-foreground">{pagination.total_count} matriculas</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => router.get('/enrollments', { ...filters, page: pagination.current_page - 1 })} disabled={pagination.current_page === 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">{pagination.current_page} de {pagination.total_pages}</span>
                  <Button variant="outline" size="icon" onClick={() => router.get('/enrollments', { ...filters, page: pagination.current_page + 1 })} disabled={pagination.current_page === pagination.total_pages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog: Matricular Aluno */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Matricular Aluno</DialogTitle>
            <DialogDescription>
              {formatCurrency(settings.enrollment_price_cents)} - Matricula vitalicia
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div>
              <Label>Buscar aluno</Label>
              <Input
                value={studentFilter}
                onChange={(e) => setStudentFilter(e.target.value)}
                placeholder="Digite o nome do aluno..."
                className="mt-1"
              />
            </div>
            <div className="border rounded-lg max-h-52 overflow-y-auto">
              {unenrolledStudents
                .filter(s => !studentFilter || s.name.toLowerCase().includes(studentFilter.toLowerCase()))
                .map(s => (
                  <button
                    key={s.id}
                    className={`w-full text-left px-3 py-2.5 text-sm border-b last:border-0 transition-colors ${selectedStudentId === String(s.id) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                    onClick={() => setSelectedStudentId(String(s.id))}
                  >
                    {s.name}
                  </button>
                ))}
              {unenrolledStudents.filter(s => !studentFilter || s.name.toLowerCase().includes(studentFilter.toLowerCase())).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum aluno encontrado</p>
              )}
            </div>

            {settings.enrollment_price_cents > 0 && (
              <div>
                <Label>Desconto</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={formatCentsInput(discountCents)}
                    onChange={(e) => setDiscountCents(parseCentsInput(e.target.value))}
                    className="pl-9"
                  />
                </div>
              </div>
            )}

            {/* Resumo */}
            {settings.enrollment_price_cents > 0 && (
              <div className="rounded-lg bg-muted/50 p-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valor matricula</span>
                  <span>{formatCurrency(settings.enrollment_price_cents)}</span>
                </div>
                {discountCents > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Desconto</span>
                    <span>-{formatCurrency(discountCents)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold border-t pt-1">
                  <span>Total</span>
                  <span>{formatCurrency(Math.max(settings.enrollment_price_cents - discountCents, 0))}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollOpen(false)}>Cancelar</Button>
            <Button onClick={enrollStudent}>Matricular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Configuracoes */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle><Settings className="w-5 h-5 inline mr-1" /> Configuracoes de Matricula</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Exigir matricula</p>
                <p className="text-xs text-muted-foreground">Aluno precisa de matricula pra comprar pacote</p>
              </div>
              <Switch checked={reqEnrollment} onCheckedChange={setReqEnrollment} />
            </div>
            {reqEnrollment && (
              <>
                <div>
                  <Label>Valor da matricula</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">R$</span>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={formatCentsInput(priceCents)}
                      onChange={(e) => setPriceCents(parseCentsInput(e.target.value))}
                      className="pl-9"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancelar</Button>
            <Button onClick={saveSettings}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
